// # 인증 — 토큰이 태어나고, 앉고, 갱신되는 곳
//
// ## 접근 토큰은 메모리, 리프레시는 브라우저가 쥔다
// 이 파일 어디에도 `localStorage` 가 없다. 접근 토큰은 변수 하나에만 살고, 탭을
// 닫으면 사라진다. 다시 들어올 때 쓰는 리프레시는 **우리가 만질 수 없는 곳**
// (`HttpOnly` 쿠키, `Path=/v1/auth`)에 있다 — XSS 가 나도 훔쳐 갈 글자가 없다.
//
// ## 그래서 인증 호출만 쿠키를 싣는다
// `api.ts` 는 `credentials: 'omit'` 이다. 쿠키가 붙는 요청은 여기 여섯 개뿐이고,
// 서버는 그 자리에 `SameSite=Strict` 를 걸어 뒀다.
//
// ## 브라우저가 없는 곳에서는 금고가 쿠키 역할을 한다
// 데스크톱·모바일에는 `HttpOnly` 쿠키가 없다(`tauri://` 오리진에는 쿠키 자체가 없다).
// 그래서 리프레시를 OS 금고(키체인·Credential Manager)에 두고 몸통에 실어 보낸다.
// **파일에 두지 않는다** — 디스크에 두면 같은 사용자로 도는 다른 프로세스가 읽는다.
//
// ## 갱신은 한 번에 하나만
// 화면 여러 곳이 동시에 토큰을 찾으면 갱신 요청이 그만큼 나간다. 리프레시는
// 한 번 쓰면 회전하므로 **두 번째 요청이 도난 감지에 걸린다.** 진행 중인 약속을
// 하나 들고 있다가 모두에게 같은 것을 돌려준다.

import { ApiError, error_body } from './api.ts'

/** 조직 주소로 찾은 문(`GET /v1/orgs/{slug}`). */
export interface OrgDoor {
  tenant: number
  slug: string
  org_name: string
}

export interface Membership {
  tenant: number
  slug: string
  org_name: string
  role: string
}

export interface TokenPair {
  access: string
  /** 웹에서는 안 쓴다(쿠키에 있다). 데스크톱·모바일이 쓴다. */
  refresh?: string
  access_ttl_secs: number
  tenant: number
  workspace: number
  user: number
  role: string
  /** 조직이 2단계 인증을 요구하는데 아직 안 켰다. 초대를 막 받아들였을 때만 선다. */
  enroll_2fa?: boolean
  /** 이 세션의 계보. 기기 목록에서 '현재 기기' 를 가린다. 옛 서버는 싣지 않는다. */
  lineage?: string
}

export interface SignedIn extends TokenPair {
  memberships: Membership[]
}

/**
 * 첫 요소(메일 코드·비밀번호)는 맞았지만 2단계가 남았다. 토큰도 쿠키도 아직 없다.
 * `enroll` 은 조직이 요구하는데 아직 안 켠 사람 — 로그인 도중에 설정부터 한다.
 */
export interface SecondPending {
  second_factor: 'verify' | 'enroll'
  ticket: string
}

export type LoginResult = SignedIn | SecondPending

/**
 * Google 로그인의 끝. 들어왔으면(또는 2단계가 남았으면) 그 결과에 주소가 붙고, 자리가 없는
 * 주소면 가입 화면을 채울 주소와 6자리 코드가 온다.
 */
export type GoogleDone =
  | (LoginResult & { email: string })
  | { signup: { email: string; code: string } }

export function is_google_signup(r: GoogleDone): r is { signup: { email: string; code: string } } {
  return 'signup' in r
}

export function is_second(r: LoginResult): r is SecondPending {
  return 'second_factor' in r
}

/** 인증 앱에 넣을 키. 손으로 넣는 Base32 와 `otpauth://` 주소. 한 번만 온다. */
export interface TotpSetup {
  secret: string
  uri: string
}

/** 2단계를 끝낸 로그인. 로그인 도중 설정까지 끝냈으면 백업 코드가 **한 번** 실린다. */
export interface SecondDone extends SignedIn {
  backup_codes?: string[]
}

export interface InvitePreview {
  email: string
  role: string
  /** 이 주소로 이미 계정이 있는가. 화면이 문구를 고르는 데만 쓴다 — 흐름은 같다. */
  has_account: boolean
  /** 어디로, 누가(3차 F81). 옛 서버에는 없다. */
  org_name?: string
  inviter?: string
}

export interface InviteAccept {
  token: string
  /**
   * 코드로 증명한 주소. 빼면 `invite_code` 로 초대장의 주소에 보낸 코드를 맞히는 것이 증명이다 —
   * 미리보기의 주소는 가린 꼴(`da***@…`)이라 화면은 이 칸을 채울 수 없다(3차 F82).
   */
  email?: string
  code: string
  display_name: string
}

/** 공유 초대 링크(G30)를 연 사람이 보는 것 — 어디로, 누가. 그 둘뿐이다. */
export interface JoinPreview {
  org_name: string
  inviter: string
}

/** 링크로 들어온다. 주소는 링크를 연 사람이 적고 코드로 증명한다. */
export interface JoinAccept {
  token: string
  email: string
  code: string
  display_name: string
}

export interface SignupPayload {
  email: string
  code: string
  slug: string
  org_name: string
  workspace_name: string
  display_name: string
  password?: string
}

export interface AuthConfig {
  /** `''` 이면 같은 오리진. 끝에 `/` 를 두지 않는다. */
  base_url: string
  /** 기기 이름. 서버가 "낯선 기기" 를 가릴 때 쓴다. */
  device?: string
  fetch?: typeof fetch
}

/** `#call` 의 셋째 인자 — 리프레시를 회전시키는 요청(F276). */
const ROTATES = true

export class AuthApi {
  #config: AuthConfig

  constructor(config: AuthConfig) {
    this.#config = config
  }

  signup_start(email: string): Promise<void> {
    return this.#call('/v1/auth/signup/start', { email })
  }

  signup_finish(payload: SignupPayload): Promise<SignedIn> {
    return this.#call('/v1/auth/signup/finish', { ...payload, device: this.#config.device })
  }

  login_start(email: string): Promise<void> {
    return this.#call('/v1/auth/login/start', { email })
  }

  login_code(email: string, code: string, tenant?: number): Promise<LoginResult> {
    return this.#call('/v1/auth/login/code', {
      email,
      code,
      tenant: tenant ?? null,
      device: this.#config.device,
    })
  }

  login_password(email: string, password: string, tenant?: number): Promise<LoginResult> {
    return this.#call('/v1/auth/login/password', {
      email,
      password,
      tenant: tenant ?? null,
      device: this.#config.device,
    })
  }

  /** 2단계를 끝낸다 — 인증 앱의 6자리나 백업 코드 하나. 이때 비로소 쿠키가 깔린다. */
  login_second(email: string, ticket: string, code: string, tenant?: number): Promise<SecondDone> {
    return this.#call('/v1/auth/login/second', {
      email,
      ticket,
      code,
      tenant: tenant ?? null,
      device: this.#config.device,
    })
  }

  /** 조직이 요구하는데 안 켠 사람이 로그인 도중에 키를 받는다. */
  login_second_setup(email: string, ticket: string): Promise<TotpSetup> {
    return this.#call('/v1/auth/login/second/setup', { email, ticket })
  }

  /**
   * 웹에서는 몸통을 비운다 — 리프레시는 쿠키가 들고 온다.
   * 쿠키가 없는 곳(데스크톱·모바일)만 `refresh` 를 실어 보낸다.
   */
  renew(refresh?: string): Promise<TokenPair> {
    return this.#call(
      '/v1/auth/refresh',
      { device: this.#config.device, ...carry(refresh) },
      ROTATES,
    )
  }

  /** 조직 갈아타기. 새 토큰 쌍이 나오고 쿠키도 다시 깔린다. */
  switch_org(tenant: number, refresh?: string): Promise<TokenPair> {
    return this.#call(
      '/v1/auth/switch',
      { tenant, device: this.#config.device, ...carry(refresh) },
      ROTATES,
    )
  }

  logout(refresh?: string): Promise<void> {
    return this.#call('/v1/auth/logout', carry(refresh))
  }

  /** 비밀번호를 잊었다. **주소를 모르는 사람에게도 204 다** — 있는 주소인지 알려 주지 않는다. */
  password_forgot(email: string): Promise<void> {
    return this.#call('/v1/auth/password/forgot', { email })
  }

  /** 메일로 온 코드로 새 비밀번호를 정한다. */
  password_reset(email: string, code: string, fresh: string): Promise<void> {
    return this.#call('/v1/auth/password/reset', { email, code, fresh })
  }

  /** 이 계정의 **모든 기기**를 끊는다. 지금 이 기기도 포함이다. */
  logout_all(refresh?: string): Promise<void> {
    return this.#call('/v1/auth/logout-all', carry(refresh))
  }

  /** 초대 링크가 살아 있는가. 인증 없이 — 링크를 받은 사람은 아직 아무것도 아니다. */
  invite_preview(token: string): Promise<InvitePreview> {
    return this.#peek('/v1/invites/preview', token)
  }

  /**
   * SSO 로그인을 시작한다. 조직 주소(slug)를 주면 IdP 주소가 온다 — 화면이 그리로 떠난다.
   * 그런 조직이 없는 것과 SSO 가 없는 것은 같은 404 다.
   */
  sso_start(org: string): Promise<{ url: string }> {
    return this.#call('/v1/auth/sso/start', { org })
  }

  /** IdP 에서 돌아와 끝낸다(`/sso/callback?code&state`). state 는 한 번만 쓰인다. */
  sso_finish(state: string, code: string): Promise<SignedIn> {
    return this.#call('/v1/auth/sso/finish', { state, code, device: this.#config.device })
  }

  /** 서버에 Google 클라이언트가 있는가. 없으면 단추를 그리지 않는다. 옛 서버(404)도 없음이다. */
  async google_enabled(): Promise<boolean> {
    const send = this.#config.fetch ?? fetch
    try {
      const res = await send(`${this.#config.base_url}/v1/auth/google`, { method: 'GET' })
      if (!res.ok) return false
      const got = (await res.json()) as { enabled?: unknown }
      return got.enabled === true
    } catch {
      return false
    }
  }

  /**
   * "Google 로 계속하기" 를 시작한다. `secret` 은 이 기기가 지은 비밀이고 끝낼 때 같은 것을 낸다 —
   * 돌아오는 주소(`maru://`)를 가로챈 앱이 코드를 쥐어도 끝내지 못하게.
   * `web` 은 이 탭이 Google 로 떠나고, `app` 은 시스템 브라우저로 연다(웹뷰 안이 아니다).
   */
  google_start(back: 'web' | 'app', secret: string): Promise<{ url: string }> {
    return this.#call('/v1/auth/google/start', { back, secret })
  }

  /** Google 에서 돌아와 끝낸다. 자리가 없는 주소면 가입 코드가 온다(`signup`). */
  google_finish(state: string, code: string, secret: string): Promise<GoogleDone> {
    return this.#call('/v1/auth/google/finish', {
      state,
      code,
      secret,
      device: this.#config.device,
    })
  }

  /** 초대받은 주소로 코드를 보낸다. 주소는 링크가 정한다 — 화면은 가린 꼴로만 안다. */
  invite_code(token: string): Promise<void> {
    return this.#call('/v1/invites/code', { token })
  }

  /** 초대를 받는다. 주소는 코드로 증명한다(`invite_code` 로 받은 코드). */
  invite_accept(payload: InviteAccept): Promise<SignedIn> {
    return this.#call('/v1/invites/accept', { ...payload, device: this.#config.device })
  }

  /** 공유 초대 링크가 살아 있는가(G30). 인증 없이 — 링크를 연 사람은 아직 아무것도 아니다. */
  join_preview(token: string): Promise<JoinPreview> {
    return this.#peek('/v1/invite-links/preview', token)
  }

  /** 링크를 연 사람이 적은 주소로 코드를 보낸다. 있는 주소인지는 가르지 않는다. */
  join_code(token: string, email: string): Promise<void> {
    return this.#call('/v1/invite-links/code', { token, email })
  }

  /** 링크로 들어온다. 역할은 늘 구성원이다. */
  join_accept(payload: JoinAccept): Promise<SignedIn> {
    return this.#call('/v1/invite-links/accept', { ...payload, device: this.#config.device })
  }

  /**
   * 조직 주소(`/w/{slug}`·`{slug}.도메인`)로 어느 조직인지 묻는다. 인증 없이 — 로그인 전 화면이 쓴다.
   * 없거나 정지된 조직은 404 다.
   */
  async org_door(slug: string): Promise<OrgDoor> {
    const send = this.#config.fetch ?? fetch
    const res = await send(`${this.#config.base_url}/v1/orgs/${encodeURIComponent(slug)}`, {
      credentials: 'omit',
    })
    if (!res.ok) {
      throw new ApiError(res.status, res.headers.get('x-request-id'), null, await error_body(res))
    }
    return (await res.json()) as OrgDoor
  }

  /** 인증 없는 GET — 링크 미리보기 둘이 쓴다. 쿠키를 싣지 않는다. */
  async #peek<T>(route: string, token: string): Promise<T> {
    const send = this.#config.fetch ?? fetch
    const res = await send(`${this.#config.base_url}${route}?token=${encodeURIComponent(token)}`, {
      credentials: 'omit',
    })
    if (!res.ok) {
      throw new ApiError(res.status, res.headers.get('x-request-id'), null, await error_body(res))
    }
    return (await res.json()) as T
  }

  async #call<T>(route: string, body: unknown, rotates = false): Promise<T> {
    const send = this.#config.fetch ?? fetch
    const res = await send(`${this.#config.base_url}${route}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      // 이 여섯 경로만 쿠키를 싣는다. 나머지 API 는 헤더 인증이라 실을 이유가 없다.
      credentials: 'include',
      // 리프레시를 회전시키는 길은 페이지가 떠나도 끝까지 간다. 새로 고침이 요청을 끊으면 서버는 이미
      // 회전했는데 새 쿠키는 버려지고, 다음 로드가 헌 쿠키를 내밀어 재사용 탐지로 계보가 끊겼다 —
      // 새로 고침을 두 번 빨리 누르면 로그아웃이었다(3차 F276).
      ...(rotates ? { keepalive: true } : {}),
    })
    if (!res.ok) {
      const again = res.headers.get('retry-after')
      throw new ApiError(
        res.status,
        res.headers.get('x-request-id'),
        again === null ? null : Number.parseInt(again, 10) || null,
        await error_body(res),
      )
    }
    if (res.status === 204) return undefined as T
    return (await res.json()) as T
  }
}

/** 없는 값을 `refresh: undefined` 로 실어 보내지 않는다 — 서버는 키의 유무로 읽는다. */
const carry = (refresh?: string) => (refresh === undefined ? {} : { refresh })

/** 만료 몇 초 전부터 미리 갱신할지. 왕복 한 번과 시계 오차를 덮을 만큼. */
export const RENEW_EARLY_SECS = 60

/** 토큰이 앉아 있는 자리. 화면은 이것만 알고 `AuthApi` 를 직접 부르지 않는다. */
export interface Seat {
  /** `ApiConfig.token` 에 그대로 넘긴다. 필요하면 조용히 갱신한다. */
  token(): Promise<string>
  /** 로그인·가입이 성공했을 때. */
  hold(pair: TokenPair): void
  /** 다시 켰을 때 쿠키(웹)나 금고(데스크톱)만으로 돌아올 수 있는지. 못 돌아오면 `false`. */
  resume(): Promise<boolean>
  /**
   * 조직 갈아타기. 리프레시가 어디 있는지(쿠키·금고)는 자리가 알고 있으므로
   * 화면이 `AuthApi` 를 직접 부르면 **데스크톱에서만 조용히 실패한다.**
   */
  switch_org(tenant: number): Promise<TokenPair>
  /** 지금 앉아 있는 사람. 없으면 `null`. */
  who(): TokenPair | null
  clear(): Promise<void>
  /** 모든 기기에서 나간다. 이 기기의 자리도 같이 비운다. */
  clear_all(): Promise<void>
}

/**
 * 리프레시를 맡아 두는 곳. `@maru/platform` 의 `Vault` 와 같은 모양이라
 * 그대로 넘기면 된다 — 이 패키지가 플랫폼 패키지를 알 필요는 없다.
 */
export interface RefreshVault {
  get(key: string): Promise<string | null>
  put(key: string, value: string): Promise<void>
  remove(key: string): Promise<void>
}

/** 금고 안에서의 이름. 바꾸면 기존 설치가 전부 한 번 로그아웃된다. */
export const REFRESH_KEY = 'maru.refresh'

export interface SeatConfig {
  /** 시험에서 시계를 손으로 돌린다. */
  now?: () => number
  /** 주면 리프레시를 여기 맡긴다. 웹은 주지 않는다(쿠키가 대신한다). */
  vault?: RefreshVault
  /**
   * 탭 사이 갱신을 한 줄로 세우는 자물쇠. 없으면 웹(쿠키)에서는 `navigator.locks` 를 쓴다.
   * 쿠키는 탭끼리 하나라서, 배포 뒤 서버가 돌아오는 순간 탭 여럿이 **같은 리프레시로 동시에**
   * 갱신하면 둘째부터 재사용으로 보여 계보가 끊긴다 — 모든 탭이 로그아웃된다(3차 F244).
   * 줄을 서면 뒤 탭은 앞 탭이 받은 새 쿠키로 간다.
   */
  lock?: <T>(run: () => Promise<T>) => Promise<T>
}

const REFRESH_LOCK = 'maru.refresh'

/** 브라우저의 Web Locks. 없으면(옛 브라우저·Node) 그냥 부른다. */
function web_lock(): (<T>(run: () => Promise<T>) => Promise<T>) | undefined {
  const locks = (globalThis.navigator as { locks?: LockManager } | undefined)?.locks
  if (locks === undefined) return undefined
  return (run) => locks.request(REFRESH_LOCK, run)
}

export function token_seat(auth: AuthApi, config: SeatConfig = {}): Seat {
  const now = config.now ?? Date.now
  const vault = config.vault
  // 금고는 기기에 하나라 프로세스가 곧 줄이다. 줄이 필요한 것은 쿠키를 나눠 쓰는 탭들이다.
  const lock = config.lock ?? (vault === undefined ? web_lock() : undefined)
  let pair: TokenPair | null = null
  let dies_at = 0
  let renewing: Promise<TokenPair> | null = null

  function sit(fresh: TokenPair) {
    pair = fresh
    dies_at = now() + fresh.access_ttl_secs * 1000
  }

  /** 회전한 리프레시를 금고에 덮어쓴다. **덮어쓰기 전에 끊기면 다음 갱신이 막힌다** —
   * 그래서 새 것을 받은 직후, 다른 일을 하기 전에 넣는다. */
  async function keep(fresh: TokenPair): Promise<void> {
    if (vault === undefined) return
    if (fresh.refresh !== undefined) await vault.put(REFRESH_KEY, fresh.refresh)
  }

  async function renew_once(): Promise<TokenPair> {
    const kept = vault === undefined ? null : await vault.get(REFRESH_KEY)
    // 금고가 비었으면 왕복할 이유가 없다. 서버가 낼 답과 같은 모양으로 던진다.
    if (vault !== undefined && kept === null) throw new ApiError(401, null, null)
    const fresh = await auth.renew(kept ?? undefined)
    sit(fresh)
    await keep(fresh)
    return fresh
  }

  /** 자리를 비우는 두 길(하나만·전부)이 같은 순서를 밟게 묶어 둔다. */
  async function leave(tell: (refresh?: string) => Promise<void>): Promise<void> {
    const kept = vault === undefined ? null : await vault.get(REFRESH_KEY).catch(() => null)
    pair = null
    dies_at = 0
    // 금고부터 비운다. 서버 호출이 실패해도 이 기기에는 남는 것이 없어야 한다.
    if (vault !== undefined) await vault.remove(REFRESH_KEY).catch(() => {})
    await tell(kept ?? undefined).catch(() => {})
  }

  function renew(): Promise<TokenPair> {
    // 이미 도는 것이 있으면 그것을 같이 기다린다.
    renewing ??= (lock === undefined ? renew_once() : lock(renew_once)).finally(() => {
      renewing = null
    })
    return renewing
  }

  return {
    async token() {
      if (pair && now() < dies_at - RENEW_EARLY_SECS * 1000) return pair.access
      return (await renew()).access
    },
    hold(fresh) {
      sit(fresh)
      // 실패해도 지금 화면은 멀쩡하다(접근 토큰은 메모리에 있다). 다음에 켤 때
      // 다시 로그인할 뿐이라 여기서 던져 로그인 성공을 실패로 보이게 하지 않는다.
      void keep(fresh).catch(() => {})
    },
    async resume() {
      try {
        await renew()
        return true
      } catch (e) {
        // 쿠키가 없거나 죽었다 — 로그인 화면으로 가면 된다. 다른 실패는 던진다.
        if (e instanceof ApiError && (e.status === 401 || e.status === 404)) return false
        throw e
      }
    },
    async switch_org(tenant) {
      const kept = vault === undefined ? null : await vault.get(REFRESH_KEY)
      const fresh = await auth.switch_org(tenant, kept ?? undefined)
      sit(fresh)
      await keep(fresh)
      return fresh
    },
    who: () => pair,
    async clear() {
      await leave((refresh) => auth.logout(refresh))
    },
    async clear_all() {
      await leave((refresh) => auth.logout_all(refresh))
    },
  }
}
